/**
 * FACE WEB DES ACHATS — `@revenuecat/purchases-js`, résolue par l'extension de plateforme de Metro.
 *
 * ⚠️ MÊME EXTENSION `.ts` QUE LA FACE NATIVE — voir l'en-tête de `purchases.ts`. Son premier rôle
 * est de ne PAS importer `react-native-purchases`, qui n'a aucune implémentation web : ses champs
 * `main`, `browser` et `react-native` pointent tous vers le même `dist/index.js` natif.
 *
 * LE CONTRAT EST CELUI DE LA FACE NATIVE, à la lettre — mêmes noms, mêmes types, mêmes états de
 * retour. Ce qui diffère est ce que chaque plateforme peut réellement tenir, et cela se voit à un
 * seul endroit : la CONFIGURATION.
 */
import { ErrorCode, Purchases, type Package, type PurchasesError } from '@revenuecat/purchases-js';
import { PRO_ANNUAL_PACKAGE, type PurchasablePlan } from '../config/purchases';
import { comparerAuMensuel, type OffreAnnuelle, type TarifPackage } from '../utils/annualOffer';
import type { PrixMensuels } from '../config/plans';
import { capturerIncident } from './sentry';
import { useUserStore } from '../store/userStore';

// EXPO_PUBLIC_OPTIONNEL: clé Web Billing, distincte des clés iOS/Android. Son absence n'est pas une
// erreur — elle signifie « achats web non branchés », exactement comme une clé Android absente
// aujourd'hui. Elle est inlinée à l'export, la poser à l'exécution de l'hébergeur n'a aucun effet.
const API_KEY = process.env.EXPO_PUBLIC_REVENUECAT_API_KEY_WEB || null;

export const BILLING_AVAILABLE = API_KEY !== null;

/**
 * CONFIGURATION PARESSEUSE — la différence structurelle entre les deux faces.
 *
 * `PurchasesConfig` exige `appUserId` : sur le web, on ne PEUT PAS configurer avant de savoir qui
 * est l'utilisateur. Le mobile configure au démarrage puis identifie par `logIn` ; ici, configurer
 * trop tôt reviendrait à créer un utilisateur anonyme et à lui rattacher l'achat — un abonné web
 * n'aurait alors rien de débloqué dans l'application, ce que toute cette unification vise à éviter.
 *
 * `configurePurchases()` ne fait donc RIEN ici, volontairement : il existe pour que l'appelant
 * s'écrive une seule fois. Tout part de `identifyPurchaser(userId)`, appelé quand la session
 * Supabase existe.
 */
export function configurePurchases(): void {
  // Sans appUserId, il n'y a rien à configurer. Voir ci-dessus.
}

let utilisateurCourant: string | null = null;

function instance(): Purchases | null {
  return Purchases.isConfigured() ? Purchases.getSharedInstance() : null;
}

/**
 * Lie les achats au compte Supabase. Premier appel : configure. Appels suivants : change
 * d'utilisateur.
 *
 * `changeUser` ET NON `identifyUser`, ET CE CHOIX N'EST PAS ANODIN. `identifyUser` crée un ALIAS
 * quand l'identifiant précédent est anonyme — donc fusionne deux comptes RevenueCat. Employé par
 * défaut, il rattacherait à un compte tout achat anonyme traînant dans le navigateur : sur un poste
 * partagé, l'abonnement de l'un passerait au compte de l'autre. Il est de surcroît marqué
 * `@experimental` dans les types du paquet. La fusion volontaire existe, elle a son propre chemin
 * nommé ci-dessous.
 */
export async function identifyPurchaser(userId: string): Promise<void> {
  if (!BILLING_AVAILABLE || !userId) return;
  try {
    if (!Purchases.isConfigured()) {
      Purchases.configure({ apiKey: API_KEY as string, appUserId: userId });
    } else if (utilisateurCourant !== userId) {
      await Purchases.getSharedInstance().changeUser(userId);
    }
    utilisateurCourant = userId;
  } catch {
    // best-effort : ne bloque jamais l'app, comme sur mobile.
  }
}

/**
 * Pas de `logOut` dans `purchases-js` — le paquet expose `LogOutWithAnonymousUserError`, signe que
 * la notion n'existe pas comme sur mobile. On oublie simplement l'utilisateur courant : la
 * prochaine session appellera `identifyPurchaser`, qui basculera par `changeUser`.
 */
export async function resetPurchaser(): Promise<void> {
  utilisateurCourant = null;
}

export async function getEntitlementPlan(): Promise<PurchasablePlan | null> {
  const p = instance();
  if (!p) return null;
  try {
    const info = await p.getCustomerInfo();
    const active = info.entitlements.active;
    if (active['pro']) return 'pro';
    if (active['plus']) return 'plus';
    return null;
  } catch {
    return null;
  }
}

export type PurchaseOutcome =
  | { status: 'success' }
  | { status: 'cancelled' }
  | { status: 'pending' }
  | { status: 'error'; message: string };

async function packagesCourants(): Promise<Package[]> {
  const p = instance();
  if (!p) return [];
  const offerings = await p.getOfferings();
  return offerings.current?.availablePackages ?? [];
}

/** Même forme que la face native : les trois champs dont dépend le calcul du prix barré. */
function tarif(pkg: Package | undefined): TarifPackage | null {
  const prix = pkg?.webBillingProduct?.currentPrice;
  if (!prix) return null;
  return {
    price: prix.amountMicros / 1_000_000,
    priceString: prix.formattedPrice,
    currencyCode: prix.currency ?? '',
  };
}

export interface Tarifs {
  annuelle: OffreAnnuelle | null;
  mensuels: PrixMensuels;
}

const AUCUN_TARIF: Tarifs = { annuelle: null, mensuels: { plus: null, pro: null } };

/**
 * LES PRIX VIENNENT DE L'OFFERING, ET DE NULLE PART AILLEURS.
 *
 * C'est le cœur de ce lot. Jusqu'ici le paywall web affichait `PRIX_REPLI` — des constantes du
 * dépôt présentées comme des prix, sans qu'aucun écran ne les distingue de vrais tarifs. Les
 * produits web portant désormais leurs vrais prix dans l'offering, ce repli n'a plus d'excuse :
 * sur le web il est SUPPRIMÉ, pas conservé en filet. Un prix faux est pire qu'un prix absent —
 * l'un fait payer autre chose que ce qui est annoncé, l'autre fait poser une question.
 */
export async function fetchTarifs(): Promise<Tarifs> {
  if (!BILLING_AVAILABLE) return AUCUN_TARIF;
  try {
    const packages = await packagesCourants();
    if (packages.length === 0) {
      capturerIncident('revenuecat_offering_vide', 'config');
      return AUCUN_TARIF;
    }
    const mensuelPro = tarif(packages.find((p) => p.identifier === 'pro'));
    const annuel = tarif(packages.find((p) => p.identifier === PRO_ANNUAL_PACKAGE));
    const mensuels: PrixMensuels = {
      plus: tarif(packages.find((p) => p.identifier === 'plus'))?.priceString ?? null,
      pro: mensuelPro?.priceString ?? null,
    };
    if (!mensuels.plus || !mensuels.pro) capturerIncident('revenuecat_packages_inapparies', 'config');
    return { annuelle: annuel ? comparerAuMensuel(annuel, mensuelPro) : null, mensuels };
  } catch {
    capturerIncident('revenuecat_offerings_illisible', 'config');
    return AUCUN_TARIF;
  }
}

export async function fetchProAnnualOffer(): Promise<OffreAnnuelle | null> {
  return (await fetchTarifs()).annuelle;
}

const MESSAGE_IDENTITE = 'Connexion à ton compte en cours. Réessaie dans un instant.';

/**
 * AVANT TOUT ACHAT, LE SDK DOIT CONNAÎTRE CE COMPTE — même garde-fou que la face native.
 *
 * `identifyPurchaser` est best-effort : si `configure` ou `changeUser` a échoué à la connexion, le
 * SDK est soit absent, soit resté sur l'utilisateur PRÉCÉDENT du navigateur. Dans le second cas
 * l'achat serait rattaché à quelqu'un d'autre sur un poste partagé ; dans le premier, il n'ouvrirait
 * aucun droit. On retente donc ici (par `changeUser`, jamais `identifyUser`), et si le SDK n'est
 * toujours pas sur le compte connecté on REFUSE d'acheter plutôt que de débiter pour un droit qui
 * n'arrivera pas — ou qui arrivera chez un autre. On compare à `utilisateurCourant`, pas seulement
 * à `isAnonymous()` : un SDK non anonyme peut très bien porter le mauvais compte.
 */
async function identiteAchatPrete(): Promise<boolean> {
  try {
    const userId = useUserStore.getState().user?.id;
    if (!userId) return false;
    const pret = (): boolean => {
      const p = instance();
      return p !== null && utilisateurCourant === userId && !p.isAnonymous();
    };
    if (pret()) return true;
    await identifyPurchaser(userId);
    return pret();
  } catch {
    return false;
  }
}

/** Achat d'un package : même contrat de retour que la face native, mêmes états. */
async function acheter(identifiant: string): Promise<PurchaseOutcome> {
  try {
    if (!(await identiteAchatPrete())) {
      capturerIncident('revenuecat_achat_identite_absente', 'paiement');
      return { status: 'error', message: MESSAGE_IDENTITE };
    }
    const p = instance();
    if (!p) return { status: 'error', message: 'Les abonnements ne sont pas disponibles pour le moment.' };
    const pkg = (await packagesCourants()).find((x) => x.identifier === identifiant);
    if (!pkg) return { status: 'error', message: 'Offre indisponible pour le moment.' };
    await p.purchase({ rcPackage: pkg });
    return { status: 'success' };
  } catch (e) {
    const err = e as PurchasesError;
    // Annulation par l'utilisateur : ce n'est pas un incident, et ça ne se remonte pas.
    // `ErrorCode.UserCancelledError` plutôt que le littéral `1` — un numéro nu dans un `catch` ne
    // se relit pas, et rien ne signalerait qu'il a changé de sens dans une version ultérieure.
    if (err?.errorCode === ErrorCode.UserCancelledError) return { status: 'cancelled' };
    capturerIncident('revenuecat_achat_web_echoue', 'config');
    return { status: 'error', message: 'L’achat a échoué. Réessaie.' };
  }
}

export async function purchasePlan(plan: PurchasablePlan): Promise<PurchaseOutcome> {
  if (!BILLING_AVAILABLE) return { status: 'error', message: 'Les abonnements ne sont pas disponibles pour le moment.' };
  return acheter(plan);
}

export async function purchaseProAnnual(): Promise<PurchaseOutcome> {
  if (!BILLING_AVAILABLE) return { status: 'error', message: 'Les abonnements ne sont pas disponibles pour le moment.' };
  return acheter(PRO_ANNUAL_PACKAGE);
}

/**
 * Pas de restauration sur le web : il n'existe pas de trousseau de plateforme à interroger.
 * L'abonnement suit le compte, donc se reconnecter suffit — et c'est ce que le message dit.
 */
export async function restorePurchases(): Promise<PurchaseOutcome> {
  return { status: 'error', message: 'Sur le web, ton abonnement suit ton compte : reconnecte-toi.' };
}
